import type { CorrectionRequest } from "../api/types";

export type AnomalyState =
  "to_justify" | "awaiting_manager" | "approved" | "rejected";

export const ANOMALY_STATE_LABELS: Record<AnomalyState, string> = {
  to_justify: "À justifier",
  awaiting_manager: "En attente du manager",
  approved: "Approuvée",
  rejected: "Rejetée",
};

// pending sans justification = à justifier ; pending justifiée = en attente du
// manager ; approved / rejected sont définitifs (rejet = anomalie ignorée).
export function anomalyState(
  a: Pick<CorrectionRequest, "status" | "justification">,
): AnomalyState {
  if (a.status === "approved") return "approved";
  if (a.status === "rejected") return "rejected";
  return a.justification.trim() ? "awaiting_manager" : "to_justify";
}

// Approuvée ou rejetée : plus rien à faire, relève de l'historique.
export function isSettled(a: Pick<CorrectionRequest, "status">): boolean {
  return a.status === "approved" || a.status === "rejected";
}

/** Traitées, de la plus récemment close à la plus ancienne. */
export function settledHistory(list: CorrectionRequest[]): CorrectionRequest[] {
  return list
    .filter(isSettled)
    .sort((a, b) =>
      (b.resolved_at ?? b.updated_at).localeCompare(
        a.resolved_at ?? a.updated_at,
      ),
    );
}

// Clé de `details` portant l'heure du fait détecté, par détecteur
// (store/anomaly_detectors.go, store/anomaly_emissions.go).
const OCCURRED_AT_KEY: Record<string, string> = {
  session_overtime: "opened_at",
  late_badge_out: "attendance_in",
  late_arrival: "attendance_in",
  early_departure: "attendance_out",
  break_too_long: "break_start",
  orphan_close: "occurred_at",
  activity_overtime: "started_at",
  activity_without_attendance: "started_at",
  missing_break: "started_at",
};

export function anomalyOccurredAt(
  a: Pick<CorrectionRequest, "anomaly_type" | "details" | "detected_at">,
): string {
  const key = OCCURRED_AT_KEY[a.anomaly_type];
  const value = key ? a.details?.[key] : undefined;
  return typeof value === "string" && value ? value : a.detected_at;
}

// Types portés par un attendance_in dont la session est restée ouverte : la
// correction badge la sortie manquante (attendance_out à proposed_at, refusé
// s'il ne suit pas l'entrée — handlers/correction_requests.go, closesOpenSession).
// late_arrival porte aussi sur un attendance_in, mais sa correction réajuste
// l'heure d'arrivée (manual_adjustment) : il n'est pas dans cette liste.
const OPEN_SESSION_TYPES = new Set(["session_overtime", "late_badge_out"]);

export function closesSession(type: string): boolean {
  return OPEN_SESSION_TYPES.has(type);
}

// Libellé de l'heure à saisir pour corriger le fait, par type.
const CORRECTION_TIME_LABELS: Record<string, string> = {
  session_overtime: "Heure de sortie réelle",
  late_badge_out: "Heure de sortie réelle",
  orphan_close: "Heure de sortie réelle",
  late_arrival: "Heure d'arrivée réelle",
  early_departure: "Heure de départ réelle",
  break_too_long: "Heure de début de pause réelle",
};

export function correctionTimeLabel(type: string): string {
  return CORRECTION_TIME_LABELS[type] ?? "Heure corrigée";
}

export function isProposedAtValid(
  a: Pick<CorrectionRequest, "anomaly_type" | "details" | "detected_at">,
  proposedAt: Date,
): boolean {
  if (Number.isNaN(proposedAt.getTime())) return false;
  if (!closesSession(a.anomaly_type)) return true;
  return proposedAt.getTime() > new Date(anomalyOccurredAt(a)).getTime();
}

// Le POST cible, pour un source_event_id, l'anomalie ouverte la plus récemment
// détectée (store.GetOpenAnomalyBySourceEvent : detected_at DESC LIMIT 1). Une
// anomalie ouverte plus ancienne sur le même pointage n'est donc pas ciblable.
function justificationTargetId(
  all: CorrectionRequest[],
  sourceEventId: string,
): string | undefined {
  let target: CorrectionRequest | undefined;
  for (const r of all) {
    if (r.source_event_id !== sourceEventId || r.status !== "pending") continue;
    if (!target || Date.parse(r.detected_at) > Date.parse(target.detected_at)) {
      target = r;
    }
  }
  return target?.id;
}

export type JustifyBlocker = "no_source" | "shared_source";

/** null = le worker peut justifier cette anomalie. */
export function justifyBlocker(
  a: CorrectionRequest,
  all: CorrectionRequest[],
): JustifyBlocker | null {
  if (!a.source_event_id) return "no_source";
  if (justificationTargetId(all, a.source_event_id) !== a.id) {
    return "shared_source";
  }
  return null;
}

/** À justifier ET justifiable par le worker : ce qu'il a réellement à faire.
 * Un constat sans pointage rattaché (ou masqué par un plus récent) revient au
 * manager et ne doit pas gonfler le compteur du worker. */
export function needsWorkerAction(
  a: CorrectionRequest,
  all: CorrectionRequest[],
): boolean {
  return anomalyState(a) === "to_justify" && justifyBlocker(a, all) === null;
}

export type TeamBucket = "to_handle" | "at_worker" | "history";

/** Onglet de la vue équipe. Ce que le collaborateur ne peut pas justifier (sans
 * pointage rattaché, ou masqué par un constat plus récent) n'attend que le
 * manager : il est à traiter au même titre qu'une justification reçue. */
export function teamBucket(
  a: CorrectionRequest,
  all: CorrectionRequest[],
): TeamBucket {
  if (isSettled(a)) return "history";
  return needsWorkerAction(a, all) ? "at_worker" : "to_handle";
}

export function canApprove(a: CorrectionRequest): boolean {
  return anomalyState(a) === "awaiting_manager" && a.source_event_id !== "";
}

// Contexte chiffré du constat (durée, seuil), lu dans `details` tel que posé par
// les détecteurs. null quand le type n'en porte pas.
export function anomalyDetailText(
  a: Pick<CorrectionRequest, "anomaly_type" | "details">,
): string | null {
  const d = a.details ?? {};
  const num = (k: string) =>
    typeof d[k] === "number" ? (d[k] as number) : undefined;
  const hours = (h: number) => `${h.toFixed(1).replace(".", ",")} h`;
  switch (a.anomaly_type) {
    case "session_overtime":
    case "activity_overtime": {
      const h = num("duration_hours");
      return h === undefined ? null : `Ouverte depuis ${hours(h)}`;
    }
    case "missing_break": {
      const h = num("worked_hours");
      const min = num("required_break_minutes");
      if (h === undefined) return null;
      return min === undefined
        ? `${hours(h)} travaillées sans pause`
        : `${hours(h)} travaillées sans pause de ${min} min`;
    }
    case "break_too_long": {
      const m = num("duration_minutes");
      const max = num("max_break_minutes");
      if (m === undefined) return null;
      return max === undefined
        ? `Pause de ${Math.round(m)} min`
        : `Pause de ${Math.round(m)} min (max ${max} min)`;
    }
    case "late_arrival":
      return typeof d.ref_start === "string"
        ? `Arrivée attendue à ${d.ref_start.slice(0, 5)}`
        : null;
    case "early_departure":
    case "late_badge_out":
      return typeof d.ref_end === "string"
        ? `Fin de journée attendue à ${d.ref_end.slice(0, 5)}`
        : null;
    default:
      return null;
  }
}

// Nom affiché du collaborateur : nom Keycloak si le backend l'a résolu, sinon
// l'identifiant tiré de l'URN.
export function workerLabel(
  a: Pick<CorrectionRequest, "worker_url" | "worker_display_name">,
): string {
  return (
    a.worker_display_name?.trim() ||
    a.worker_url.replace("urn:senacle:worker:", "")
  );
}
